import { nanoid } from 'nanoid';
import * as React from 'react';
import type { FileRejection } from 'react-dropzone';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps, FormErrorsWithArray } from 'redux-form';
import { reduxForm, FieldArray, getFormSyncErrors, getFormValues, Field } from 'redux-form';

import type { W_Job_FindInvoiceById_VM } from 'ab-api/web/job/findInvoiceById';
import type { W_Job_FindUserGroupAndPMForInvoicing_VM } from 'ab-api/web/job/findUserGroupAndPMForInvoicing';

import type { PendingAttachmentVM } from 'ab-viewModels/attachment/pendingAttachment.viewModel';
import type { ContactForInvoicesVM } from 'ab-viewModels/contact/contactsForInvoices.viewModel';
import type InvoiceCodesVM from 'ab-viewModels/workRequest/invoiceCodes.viewModel';
import type InoviceReminderRecipientsVM from 'ab-viewModels/workRequest/invoiceReminderRecipients';

import { getId } from '@acceligentllc/shared/utils/array';
import { isValidEmail } from '@acceligentllc/shared/utils/email';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { BlobStorageContainer } from '@acceligentllc/shared/enums/blobStorageContainer';
import { FileType, IMAGE_FILE_TYPES } from '@acceligentllc/shared/enums/fileType';
import { InvoiceBillingLookupType } from '@acceligentllc/shared/enums/invoiceBillingLookupType';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import { dollarFormatter } from 'af-utils/format.util';

import InvoiceStatusDisplay from 'ab-enums/invoiceStatusDisplay.enum';

import * as FORMS from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import { Button, SegmentLabel, errorToast, loadingToast, successToast } from '@acceligentllc/storybook';

import Checkbox from 'af-fields/Checkbox';
import DateInput from 'af-fields/DateInput';
import Input from 'af-fields/Input';
import Textarea from 'af-fields/Textarea';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ConfirmationModal from 'af-components/ConfirmationModal';
import FileUploadMessageWithTypes from 'af-components/Dropzone/FileUploadMessageWithTypes';
import DropzoneWithButton from 'af-components/DropzoneWithButton';
import type { RouteParams } from 'af-components/Header';
import LockedValue from 'af-components/LockedValue';
import SubmitButton from 'af-components/SubmitButton';

import * as AttachmentActions from 'af-actions/attachment';
import * as ContactActions from 'af-actions/contacts';
import * as JobActions from 'af-actions/jobs';

import type { AttachmentFM } from './formModel';
import { InvoiceFM } from './formModel';
import type { OwnProps as InstallmentArrayProps, InstallmentFM } from './Installments/InstallmentItems';
import InstallmentItems from './Installments/InstallmentItems';
import styles from './styles.module.scss';
import { JobPreviewTabRoutes } from '../../types';
import type { OwnProps as AttachmentArrayProps } from '../Attachments/AttachmentItems';
import AttachmentItems from '../Attachments/AttachmentItems';
import type { OwnProps as BillingContactArrayProps } from '../BillingContacts/BillingContactArray';
import BillingContactArray from '../BillingContacts/BillingContactArray';

type ValidationError = {
	[index: number]: { [key: string]: string; };
};
interface PathParams {
	jobId: string;
	invoiceId: string;
}

type OwnProps = CustomRouteComponentProps<RouteParams & PathParams>;

type FormOwnProps = ConnectedProps<typeof connector> & OwnProps;
type Props = FormOwnProps & InjectedFormProps<InvoiceFM>;

const InvoiceSectionInvoiced: React.FC<Props> = (props) => {

	const {
		updateInvoicedInvoice,
		getInvoiceById,
		areAutomaticRemindersEnabled,
		sendManualReminderForInvoice,
		getAllInvoiceCodesForJob,
		uploadPendingAttachments,
		deletePendingAttachments,
		history,
		match: { params: { jobId, invoiceId } },
		location: { state: { orgAlias, jobCode, backToAccountingPage } },
		companyName,
		submitting,
		initialize,
		change,
		currentFormValues,
		errors,
		initialized,
		uploadProgress,
		isSendingInvoiceNotificationsEnabled,
		pristine,
		findContactsForInvoices,
		handleSubmit,
		getInvoiceReminderRecipients,
		getUserGroupAndPM,
	} = props;

	const [startingBillingContacts, setStartingBillingContacts] = React.useState<W_Job_FindInvoiceById_VM['billingContacts']>([]);
	const [automaticRemindersEnabled, setAutomaticRemindersEnabled] = React.useState<boolean>(false);
	const [hasInstallmentsInEditMode, setHasInstallmentsInEditMode] = React.useState(false); // Can't use dirty or pristine since we use change with initialization
	const [showLeaveModal, setShowLeaveModal] = React.useState<boolean>(false);
	const [showManualReminderModal, setShowManualReminderModal] = React.useState<boolean>(false);
	const [totalPriceChanged, setTotalPriceChanged] = React.useState<boolean>(false);
	const [showChangePriceModal, setShowChangePriceModal] = React.useState<boolean>(false);
	const [billingContacts, setBillingContacts] = React.useState<Nullable<ContactForInvoicesVM[]>>(null);
	const [isLoading, setIsLoading] = React.useState(false);
	const [existingInvoiceCodes, setExistingInvoiceCodes] = React.useState<Nullable<InvoiceCodesVM[]>>([]);
	const [additionalRecipients, setAdditionalRecipients] = React.useState<Nullable<InoviceReminderRecipientsVM[]>>(null);
	const [billingContactsForTable, setBillingContactsForTable] = React.useState<W_Job_FindInvoiceById_VM['billingContactMethods']>([]);
	const [emailReminderSent, setEmailReminderSent] = React.useState(false);
	const [userGroupAndPM, setUserGroupAndPM] = React.useState<Nullable<W_Job_FindUserGroupAndPMForInvoicing_VM>>(null);

	const fetchContacts = React.useCallback(async () => {
		const fetchedContacts = await findContactsForInvoices();
		setBillingContacts(fetchedContacts);
	}, [findContactsForInvoices]);

	// On mount
	React.useEffect(() => {
		const _initialize = async () => {
			const usrGroupPM = await getUserGroupAndPM(+jobId);
			setUserGroupAndPM(usrGroupPM);
		};
		_initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (!billingContacts) {
			fetchContacts();
		}
	}, [billingContacts, fetchContacts]);

	const ALLOWED_ATTACHMENT_FILE_TYPES_MIME = {
		'image/*': IMAGE_FILE_TYPES,
		'application/*': [
			FileType.PDF,
			FileType.XLSX,
			FileType.XLS,
			FileType.DOCX,
			FileType.DOC,
		],
		'text/*': [FileType.CSV],
	};

	const LEAVE_PAGE_MODAL_BODY = (
		<>
			You are about to leave this page without saving.
			<br />
			Changes that you made will not be saved.
		</>
	);

	const CHANGE_TOTAL_PRICE_MODAL_BODY = (
		<>
			You are about change the total price for invoiced invoice.
			<br />
			Are you sure?
		</>
	);

	const getSendReminderModalBody = React.useCallback(() => {
		if (currentFormValues?.billingContacts?.length) {
			return (<>
				Payment reminder will be sent to following billing contact(s):
				<br />
				{
					currentFormValues.billingContacts.reduce((_acc, _contact, _index) => {
						if (!_contact) {
							return _acc;
						}

						let contactValue = '';
						let contactEmails: Nullable<string> = null;
						if (_contact.type === InvoiceBillingLookupType.EMAIL_ONLY) {
							contactValue = _contact.email;
						} else if (_contact.type === InvoiceBillingLookupType.CONTACT) {
							contactValue = _contact.contact.fullName;
							const selectedEmails = new Set(_contact.contact.contactEmailIds);
							const selectedEmailsValues = _contact.contact.emails.reduce((_emails, _email) => {
								if (selectedEmails.has(_email.id)) {
									_emails.push(_email.value);
								}
								return _emails;
							}, [] as string[]);
							contactEmails = ` (${selectedEmailsValues.join(', ')})`;
						}

						_acc.push(
							<React.Fragment key={_index}>
								<b>
									{contactValue}
								</b>
								{contactEmails && (
									<span>
										{contactEmails}
									</span>)
								}
								{_index !== currentFormValues.billingContacts.length - 1 ? ', ' : '.'}
							</React.Fragment>
						);
						return _acc;
					}, [] as React.JSX.Element[])
				}
				{
					userGroupAndPM?.userGroupName &&
					<>
						< br />
						< br />
						user group: <b>{userGroupAndPM?.userGroupName}</b>
					</>
				}
				{
					userGroupAndPM?.pmFirstName &&
					<>
						< br />
						< br />
						and project manager: <b>{userGroupAndPM?.pmFirstName} {userGroupAndPM?.pmLastName} ({userGroupAndPM?.pmEmail})</b>
					</>
				}
				{
					!!additionalRecipients?.length &&
					<>
						< br />
						< br />
						and recipients: {additionalRecipients.map((_recipient, index) => {
							return <b key={index}>
								{` ${_recipient.firstName} ${_recipient.lastName} ${_recipient.email ? ` (${_recipient.email})` : ''}${additionalRecipients.length - 1 === index ? '' : ','}`}
							</b>;
						})}
					</>
				}
				< br />
				<br />
				Send reminder?
			</>);
		}
	}, [currentFormValues, userGroupAndPM, additionalRecipients]);

	const onTotalAmountChange = React.useCallback((value: string) => {
		const outstandingDebt = +(value ?? 0) - (currentFormValues?.paidAmount ?? 0);
		if (currentFormValues.installments.length > 0) {
			if (outstandingDebt < 0) {
				change('status', InvoiceStatusDisplay.OVERPAID);
			} else if (outstandingDebt === 0) {
				change('status', InvoiceStatusDisplay.PAID);
			} else {
				change('status', InvoiceStatusDisplay.PARTIALLY_PAID);
			}
		}
		change('outstandingDebt', outstandingDebt < 0 ? 0 : outstandingDebt);
		setTotalPriceChanged(true);
	}, [change, currentFormValues]);

	const closeLeaveModal = React.useCallback(() => setShowLeaveModal(false), []);

	const closeManualReminderModal = React.useCallback(() => setShowManualReminderModal(false), []);

	const closeChangePriceModal = React.useCallback(() => setShowChangePriceModal(false), []);

	const openChangePriceModal = React.useCallback(() => setShowChangePriceModal(true), []);

	const fetchAndInitializeData = React.useCallback(async () => {
		setIsLoading(true);
		// get company setting for notifications
		const [
			areAutomaticInvoiceRemindersEnabledForCompany,
			invoiceCodes,
			invoiceVM,
			fetchedContacts,
			additionalInvoiceRecipients,
		] = await Promise.all([
			areAutomaticRemindersEnabled(),
			getAllInvoiceCodesForJob(+jobId),
			getInvoiceById(+jobId, +invoiceId),
			findContactsForInvoices(),
			getInvoiceReminderRecipients(+jobId),
		]);

		setAutomaticRemindersEnabled(areAutomaticInvoiceRemindersEnabledForCompany);
		setExistingInvoiceCodes(invoiceCodes);
		if (!invoiceVM) {
			throw new Error('Invoice not found');
		}
		initialize(InvoiceFM.fromVMtoFM(invoiceVM));
		setStartingBillingContacts(invoiceVM.billingContacts);
		setBillingContactsForTable(invoiceVM.billingContactMethods);
		setBillingContacts(fetchedContacts);
		setAdditionalRecipients(additionalInvoiceRecipients);
		setIsLoading(false);
	}, [areAutomaticRemindersEnabled,
		getAllInvoiceCodesForJob,
		jobId,
		getInvoiceById,
		invoiceId,
		initialize,
		findContactsForInvoices,
		getInvoiceReminderRecipients]);

	// in order to be used only once the component is mounted, deps are left empty
	React.useEffect(() => {
		fetchAndInitializeData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const resolveInvoiceStatus = React.useCallback((totalAmount: number, paidAmount: number) => {
		const differenceBetweenOwedAndPaid = totalAmount - paidAmount;
		if (differenceBetweenOwedAndPaid < 0) {
			return InvoiceStatusDisplay.OVERPAID;
		} else if (differenceBetweenOwedAndPaid === 0) {
			return InvoiceStatusDisplay.PAID;
		} else if (differenceBetweenOwedAndPaid > 0 && differenceBetweenOwedAndPaid < totalAmount) {
			return InvoiceStatusDisplay.PARTIALLY_PAID;
		} else {
			return InvoiceStatusDisplay.INVOICED;
		}
	}, []);

	const updateTotalPaid = React.useCallback(async (installments: InstallmentFM[]) => {
		if (installments && currentFormValues) {
			const recalculatedTotalPaid = InvoiceFM.recalculatePaidAmount(installments);
			change('paidAmount', recalculatedTotalPaid);
			const totalPaidDiff = currentFormValues.totalAmount - recalculatedTotalPaid;
			change('outstandingDebt', totalPaidDiff > 0 ? totalPaidDiff : 0);
			change('status', resolveInvoiceStatus(currentFormValues.totalAmount, recalculatedTotalPaid));
		}
	}, [change, currentFormValues, resolveInvoiceStatus]);

	const clearPendingAttachments = React.useCallback(async () => {
		if (currentFormValues?.uploadedAttachmentIds?.length) {
			await deletePendingAttachments({ attachmentIds: currentFormValues?.uploadedAttachmentIds });
		}
	}, [currentFormValues?.uploadedAttachmentIds, deletePendingAttachments]);

	const onBack = React.useCallback(() => {
		setShowLeaveModal(true);
	}, []);

	const onSendManualReminder = React.useCallback(() => {
		setShowManualReminderModal(true);
	}, []);

	const updateBillingContactsEmailStatus = React.useCallback(async () => {
		const contactsWithUpdatedStatus: W_Job_FindInvoiceById_VM['billingContactMethods'] = [];
		const dateNow = TimeUtils.formatDate(new Date(), TimeFormat.ISO_DATETIME);
		for (let i = 0; i < billingContactsForTable.length; i++) {
			change(`billingContactMethods[${i}].lastReminderSent`, dateNow);

			contactsWithUpdatedStatus.push({
				...billingContactsForTable[i],
				lastReminderSent: dateNow,
			});
		}

		setBillingContactsForTable(contactsWithUpdatedStatus);
		setEmailReminderSent(true);

	}, [billingContactsForTable, change]);

	const sendManualReminder = React.useCallback(async () => {
		const toastId = `${invoiceId}`;

		const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);
		loadingToast(toastId, { title: 'Sending invoice reminder to billing contacts...', timestamp: startingTimestamp });

		try {
			await sendManualReminderForInvoice(+invoiceId);
			successToast(toastId, { title: 'Reminders successfully sent to billing contacts', description: currentFormValues.invoiceCode, timestamp: startingTimestamp });
			updateBillingContactsEmailStatus();
		} catch (error) {
			if (error.errors.billingContacts) {
				errorToast(toastId, { title: 'There was a problem sending reminders', description: error.errors.billingContacts, timestamp: startingTimestamp });
			}
		}

	}, [currentFormValues?.invoiceCode, invoiceId, sendManualReminderForInvoice, updateBillingContactsEmailStatus]);

	const goBackToTable = React.useCallback(() => {
		clearPendingAttachments();
		if (backToAccountingPage) {
			history.push({
				pathname: CLIENT.COMPANY.ACCOUNTING.INVOICES.TABLE(orgAlias, companyName),
				state: {
					orgAlias,
				},
			});
		} else {
			history.push({
				pathname: CLIENT.COMPANY.JOBS.PREVIEW_TAB(orgAlias, companyName, jobId, JobPreviewTabRoutes.INVOICES),
				state: {
					orgAlias,
				},
			});
		}
	}, [companyName, history, jobId, orgAlias, backToAccountingPage, clearPendingAttachments]);

	const onSubmit = React.useCallback(async () => {
		if (currentFormValues.id) {
			await updateInvoicedInvoice(+jobId, currentFormValues.id, InvoiceFM.fromFMtoRM(currentFormValues));
			goBackToTable();
		} else {
			throw new Error('Missing invoice id');
		}
	}, [currentFormValues, goBackToTable, jobId, updateInvoicedInvoice]);

	const renderPaidAmount = () => {
		if (currentFormValues.status === InvoiceStatusDisplay.PAID) {
			return (
				<LockedValue label="Total Paid"
					value={dollarFormatter.format(currentFormValues.paidAmount)}
					valueClassName={styles['invoice-form__tab-content__form__section__row__column-total-paid__paid']}
				/>
			);
		}
		if (currentFormValues.status === InvoiceStatusDisplay.OVERPAID) {
			const overpaidAmount = dollarFormatter.format(currentFormValues.paidAmount - currentFormValues.totalAmount);
			const innerChildNode = (
				<>
					<span>{dollarFormatter.format(currentFormValues.paidAmount)} </span>
					<span className={styles['invoice-form__tab-content__form__section__row__column-total-paid__overpaid']}>(+{overpaidAmount})</span>
				</>
			);
			return (
				<LockedValue label="Total Paid"
					value={innerChildNode}
					valueClassName={styles['invoice-form__tab-content__form__section__row__column-total-paid']}
				/>
			);
		}

		return (
			<>
				<LockedValue label="Total Paid"
					value={dollarFormatter.format(currentFormValues.paidAmount)}
				/>
			</>
		);
	};

	const renderInvoiceStatus = React.useCallback((invoiceStatus: InvoiceStatusDisplay) => {
		const cellStyle: string[] = [];
		cellStyle.push(styles['invoice__status-cell']);

		switch (invoiceStatus) {
			case InvoiceStatusDisplay.DRAFT: {
				cellStyle.push(styles['invoice__status-cell--draft']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-draft" />
						<b>Draft</b>
					</span>
				);
			}
			case InvoiceStatusDisplay.INVOICED: {
				cellStyle.push(styles['invoice__status-cell--invoiced']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-invoice_status_invoiced" />
						<b>Invoiced</b>
					</span>
				);
			}
			case InvoiceStatusDisplay.PARTIALLY_PAID: {
				cellStyle.push(styles['invoice__status-cell--partially-paid']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-check" />
						<b>Partially paid</b>
					</span>
				);
			}
			case InvoiceStatusDisplay.PAID: {
				cellStyle.push(styles['invoice__status-cell--paid']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-check" />
						<b>Paid</b>
					</span>
				);
			}
			case InvoiceStatusDisplay.OVERPAID: {
				cellStyle.push(styles['invoice__status-cell--overpaid']);
				return (
					<span className={cellStyle.join(' ')}>
						<span className="icon-check" />
						<b>Overpaid</b>
					</span>
				);
			}
			default: {
				cellStyle.push(styles['invoice__empty-cell']);
				return (
					<span className={cellStyle.join(' ')}>
						N/A
					</span>
				);
			}
		}
	}, []);

	const handleAttachmentsUpload = React.useCallback((attachmentIds: number[]) => {
		change('uploadedAttachmentIds', [...attachmentIds]);
	}, [change]);

	const handleAttachmentDeleted = React.useCallback((attachmentId: number) => {
		const indexOfAttachmentId = currentFormValues.uploadedAttachmentIds?.findIndex((_id) => _id === attachmentId);
		if (indexOfAttachmentId !== undefined && indexOfAttachmentId !== -1 && currentFormValues.uploadedAttachmentIds) {
			const nextAttachmentIds = [...currentFormValues.uploadedAttachmentIds];
			nextAttachmentIds.splice(indexOfAttachmentId, 1);
			change('uploadedAttachmentIds', nextAttachmentIds);
		}
	}, [change, currentFormValues]);

	const handleAttachmentsChanged = React.useCallback((changedAttachments: AttachmentFM[]) => {
		change('attachments', changedAttachments);
	}, [change]);

	const handleAttachmentUploadStart = React.useCallback((correlationId: string) => {
		const nextUploadingAttachments = { ...currentFormValues.uploadingAttachments };
		nextUploadingAttachments[correlationId] = true;
		change('uploadingAttachments', nextUploadingAttachments);
	}, [change, currentFormValues]);

	const handleAttachmentUploadEnd = React.useCallback((correlationId: string) => {
		const nextUploadingAttachments = { ...currentFormValues.uploadingAttachments };
		delete nextUploadingAttachments[correlationId];
		change('uploadingAttachments', nextUploadingAttachments);
	}, [change, currentFormValues]);

	const handleFileUpload = React.useCallback(async (acceptedFiles: File[], fileRejections: FileRejection[]) => {

		if (fileRejections.length > 0) {
			let message = `Uploading of ${fileRejections[0].file.name} failed.`;
			if (fileRejections[0].errors[0].code === 'file-invalid-type') {
				message = 'Uploading failed - File type not supported';
			}
			errorToast(fileRejections[0].file.name, { title: message, timestamp: TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG) });
			return;
		}

		const correlationId = nanoid();
		handleAttachmentUploadStart?.(correlationId);
		let uploadedAttachments: PendingAttachmentVM[] = [];

		const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);
		loadingToast(acceptedFiles[0].name, { title: `Uploading ${acceptedFiles[0].name}`, timestamp: startingTimestamp });

		try {
			uploadedAttachments = await uploadPendingAttachments({ requestFiles: acceptedFiles }, BlobStorageContainer.INVOICES_ATTACHMENTS);
			successToast(acceptedFiles[0].name, { title: `${acceptedFiles[0].name} has successfully been uploaded.`, timestamp: startingTimestamp });
		} finally {
			handleAttachmentUploadEnd?.(correlationId);
		}

		const nextUploadedAttachmentIds = [...(currentFormValues.uploadedAttachmentIds ?? []), ...uploadedAttachments.map(getId)];
		handleAttachmentsChanged([
			...(currentFormValues.attachments ?? []),
			...(uploadedAttachments.map((_att) => { return { ..._att, fileName: _att.name }; })),
		]);
		handleAttachmentsUpload(nextUploadedAttachmentIds);
	}, [
		currentFormValues,
		handleAttachmentUploadEnd,
		handleAttachmentUploadStart,
		handleAttachmentsChanged,
		handleAttachmentsUpload,
		uploadPendingAttachments,
	]);

	const saveForm = React.useCallback(() => {
		if (totalPriceChanged) {
			openChangePriceModal();
		} else {
			onSubmit();
		}
	}, [onSubmit, openChangePriceModal, totalPriceChanged]);

	const checkUniqueInvoiceCode = React.useCallback((value: string) => {

		if (!value) {
			return 'Invoice ID is required';
		}

		if (!existingInvoiceCodes) {
			return true;
		}

		if (existingInvoiceCodes.some((_item) => _item.invoiceCode === value && (!invoiceId || +invoiceId !== _item.id))) {
			return 'Invoice ID already taken';
		}
	}, [existingInvoiceCodes, invoiceId]);

	const currentFormBillingContactMethodsErrors = React.useMemo(() => {
		const emailValidation: ValidationError = {};

		if (currentFormValues) {
			for (let i = 0; i < currentFormValues.billingContactMethods.length; i++) {
				const _bcm = currentFormValues.billingContactMethods[i];
				if (!_bcm) {
					emailValidation[i] = { 'fullName': 'Please select value' };
				} else if (_bcm.email !== '' && !_bcm.email.includes('@')) {
					emailValidation[i] = { 'fullName': 'Please use email format: user.name@email.com' };
				} else if (_bcm.email !== '' && !isValidEmail(_bcm.email)) {
					emailValidation[i] = { 'fullName': 'Email not valid' };
				} else if (_bcm.email === '') {
					emailValidation[i] = { 'fullName': 'Please select value' };
				} else {
					emailValidation[i] = {};
				}
			}
		}
		return emailValidation;
	}, [currentFormValues]);

	const handleDateCreatedChange = React.useCallback((e) => {
		const date = new Date(e.target.value);
		date.setDate(date.getDate() + 30);
		change('dueDate', date);
	}, [change]);

	const renderInvoiceSections = () => {

		if (!currentFormValues) {
			return null;
		}

		const uploading = uploadProgress !== 100 && uploadProgress !== 0;

		const style: Partial<React.CSSProperties> | undefined = uploading
			? { marginTop: '15px', padding: '1px', background: `linear-gradient(to right, #9FCAFF ${uploadProgress}%, transparent ${uploadProgress}% 100%)` }
			: { marginTop: '15px' };

		const manualReminderTooltipMessage =
			<>
				<strong>Send Manual Reminder</strong> is disabled until all changes on the form have been saved.
				<br />
				Reminder will be sent to the emails listed bellow as billing contacts.
			</>;

		return (
			<>
				<div className={styles['invoice-form__tab-content__form']}>
					<div className={styles['invoice-form__submit-section']}>
						<div className={styles['invoice-form__submit-section__invoice-code-and-reminder']}>
							<span className={styles['invoice-form__submit-section__invoice-code-and-reminder__code']}>
								{currentFormValues.invoiceCode}
							</span>
							{
								isSendingInvoiceNotificationsEnabled &&
								<Button
									disabled={!pristine
										|| !startingBillingContacts.length
										|| currentFormValues.status === InvoiceStatusDisplay.PAID
										|| currentFormValues.status === InvoiceStatusDisplay.OVERPAID}
									icon="Email"
									label="Send Manual Reminder"
									onClick={onSendManualReminder}
									style="secondary"
									tooltip={manualReminderTooltipMessage}
									tooltipPosition="right"
								/>
							}
						</div>
						<div className={styles.buttonActions}>
							<Button
								label="Back"
								onClick={onBack}
								style="secondary"
							/>
							<SubmitButton
								disabled={!isSaveFormEnabled}
								label="Save"
								onClick={handleSubmit(saveForm)}
								reduxFormSubmitting={submitting}
							/>
						</div>
					</div>
					<div className={styles['invoice-form__tab-content__form__section']}>
						<div className="m-b-m">
							<SegmentLabel
								label="General Info"
								tooltip="Invoice details including the invoice ID, creation and due dates, current status, and any additional notes relevant to this invoice. The due date is automatically set to 30 days after the creation date."
							/>
						</div>
						<div className={styles['invoice-form__tab-content__form__section__row']}>
							<div className={styles['invoice-form__tab-content__form__section__row__column-id']}>
								<Field
									component={Input}
									id="invoiceCode"
									isRequired={true}
									label="Invoice ID"
									name={InvoiceFM.getAttributeName('invoiceCode')}
									placeholder="Invoice ID"
									required={true}
									type="text"
									validate={checkUniqueInvoiceCode}
								/>
							</div>
							<div className={styles['invoice-form__tab-content__form__section__row__column-date']}>
								<Field
									component={DateInput}
									dateFormat={TimeFormat.DATE_ONLY}
									id="dateCreated"
									isRequired={true}
									label="Date Created"
									name={InvoiceFM.getAttributeName('dateCreated')}
									onChange={handleDateCreatedChange}
									originalDateFormat={TimeFormat.DATE_ONLY}
									placeholderText={TimeFormat.DATE_ONLY}
									required={true}
								/>
							</div>
							<div className={styles['invoice-form__tab-content__form__section__row__column-date']}>
								<Field
									component={DateInput}
									dateFormat={TimeFormat.DATE_ONLY}
									id="dueDate"
									isRequired={true}
									label="Due Date"
									name={InvoiceFM.getAttributeName('dueDate')}
									originalDateFormat={TimeFormat.DATE_ONLY}
									placeholderText={TimeFormat.DATE_ONLY}
									required={true}
								/>
							</div>
							<div className={styles['invoice-form__tab-content__form__section__row__column-invoice']}>
								<LockedValue label="Invoice Status" value={renderInvoiceStatus(currentFormValues.status)} />
							</div>
						</div>
						<div className={styles['invoice-form__tab-content__form__section__row']}>
							<div className={styles['invoice-form__tab-content__form__section__row__column-note']}>
								<Field
									component={Textarea}
									id="note"
									label="Note"
									maxCharacters={300}
									name={InvoiceFM.getAttributeName('note')}
									rows={1}
									type="text"
								/>
							</div>
						</div>
					</div>
					<div className={styles['invoice-form__tab-content__form__section']}>
						<div className="m-b-m">
							<SegmentLabel
								label="BILLING CONTACTS"
								tooltip="Contacts designated to receive this invoice. The Billing Contact from Job Details is included here by default."
							/>
						</div>
						<FieldArray<BillingContactArrayProps>
							availableBillingContacts={billingContacts ?? []}
							billingContactMethods={billingContactsForTable}
							change={change}
							component={BillingContactArray}
							emailReminderSent={emailReminderSent}
							name={InvoiceFM.getAttributeName('billingContactMethods')}
							rerenderOnEveryChange={true}
							validationErrors={currentFormBillingContactMethodsErrors}
						/>
					</div>
					{
						isSendingInvoiceNotificationsEnabled &&
						<div className={styles['invoice-form__tab-content__form__section']}>
							<div className="m-b-m">
								<SegmentLabel
									label="REMINDERS"
									tooltip="Automatic email notification settings for this invoice, controlling whether billing contacts and internal users receive reminders when the invoice is issued or overdue."
								/>
							</div>
							<Field
								component={Checkbox}
								inline={true}
								isDisabled={true}
								label="Send email to billing contacts when invoicing"
								name="sendReminderOnInvoice"
							/>
							{
								automaticRemindersEnabled &&
								<>
									<Field
										component={Checkbox}
										inline={true}
										label="Exclude internal users from automatic reminders"
										name="excludeInternalUsersFromAutomaticReminders"
										tooltipMessage="User group, project manager and invoice reminder recipients defined on job level."

									/>
									<Field
										component={Checkbox}
										inline={true}
										label="Exclude billing contacts from automatic reminders"
										name="excludeBillingContactsFromAutomaticReminders"
									/>
								</>
							}
						</div>
					}
					<div className={styles['invoice-form__tab-content__form__section']}>
						<div className="m-b-m">
							<SegmentLabel
								label="PAYMENT INFORMATION"
								tooltip="Financial summary of this invoice: total amount, payments received, outstanding balance, and retained amount. Total Paid and Outstanding Balance are auto-calculated from the invoice total and installments."
							/>
						</div>
						<div className={styles['invoice-form__tab-content__form__section__row']}>
							<div className={styles['invoice-form__tab-content__form__section__row__column-2']}>
								<Field
									addonAfter="$"
									component={Input}
									id="totalAmount"
									isRequired={true}
									isZeroIfEmpty={true}
									label="Total"
									name={InvoiceFM.getAttributeName('totalAmount')}
									onValueChange={onTotalAmountChange}
									required={true}
									type="number"
									withThousandsSeparator={true}
								/>
							</div>
							<div className={styles['invoice-form__tab-content__form__section__row__column-total-paid']}>
								{renderPaidAmount()}
							</div>
							<div className={styles['invoice-form__tab-content__form__section__row__column-2']}>
								<LockedValue label="Outstanding" value={dollarFormatter.format(currentFormValues.outstandingDebt)} />
							</div>
							<div className={styles['invoice-form__tab-content__form__section__row__column-2']}>
								<Field
									addonAfter="$"
									component={Input}
									id="retainedAmount"
									label="Retained Amount"
									name="retainedAmount"
									type="number"
									withThousandsSeparator={true}
								/>
							</div>
						</div>
					</div>
					<div className={styles['invoice-form__tab-content__form__section']}>
						<div className="m-b-m">
							<SegmentLabel
								label="INSTALLMENTS"
								tooltip="A record of all partial payments associated with this invoice, including the amount, date paid, and any notes per installment."
							/>
						</div>
						<div className={styles['invoice-form__tab-content__form__section__installment-notice']}>
							Please note that when You add an installment in the table,
							You still have to click Save button in the right corner of the page for changes to be saved.
						</div>
						<FieldArray<InstallmentArrayProps>
							component={InstallmentItems}
							errors={errors}
							initialized={initialized}
							invoiceId={currentFormValues.id}
							name="installments"
							rerenderOnEveryChange={true}
							setUnsavedChanges={setHasInstallmentsInEditMode}
							updateTotalPaid={updateTotalPaid}
						/>
					</div>
					<div className={styles['invoice-form__tab-content__form__section']}>
						<div className="m-b-m">
							<SegmentLabel
								label="ATTACHMENTS"
								tooltip="All attachments associated with this invoice. They get sent in email reminders."
							/>
						</div>
						<FieldArray<AttachmentArrayProps>
							component={AttachmentItems}
							errors={errors}
							initialized={initialized}
							name="attachments"
							onDelete={handleAttachmentDeleted}
							rerenderOnEveryChange={true}
						/>
						<DropzoneWithButton
							allowedTypes={ALLOWED_ATTACHMENT_FILE_TYPES_MIME}
							onDrop={handleFileUpload}
							style={style}
						>
							<FileUploadMessageWithTypes
								uploading={uploading}
							/>
						</DropzoneWithButton>
					</div>
				</div>
				<ConfirmationModal
					body={LEAVE_PAGE_MODAL_BODY}
					closeModal={closeLeaveModal}
					closeText="Stay"
					confirmAction={goBackToTable}
					confirmText="Leave"
					modalStyle="danger"
					showModal={showLeaveModal}
					size="md"
					title="Leave Page?"
				/>
				<ConfirmationModal
					body={getSendReminderModalBody()}
					closeModal={closeManualReminderModal}
					closeText="Cancel"
					confirmAction={sendManualReminder}
					confirmText="Send"
					modalStyle="info"
					showModal={showManualReminderModal}
					size="md"
					title="Send Reminder?"
				/>
				<ConfirmationModal
					body={CHANGE_TOTAL_PRICE_MODAL_BODY}
					closeModal={closeChangePriceModal}
					closeText="Cancel"
					confirmAction={onSubmit}
					confirmText="I understand"
					modalStyle="danger"
					showModal={showChangePriceModal}
					size="md"
					title="Change Total Price?"
				/>
			</>
		);
	};

	const isSaveFormEnabled = React.useMemo(() => {
		return InvoiceFM.saveFormEnabled(currentFormValues, errors, hasInstallmentsInEditMode);
	}, [currentFormValues, errors, hasInstallmentsInEditMode]);

	if (isLoading) {
		return (
			<Breadcrumbs
				items={[
					{ label: 'Jobs', url: CLIENT.COMPANY.JOBS.TABLE(orgAlias, companyName) },
					{ label: jobCode, url: CLIENT.COMPANY.JOBS.PREVIEW(orgAlias, companyName, jobId) },
					{ label: 'Invoices', onClick: goBackToTable },
					{ label: 'Loading' },
				]}
			/>
		);
	}

	return (
		<>
			<Breadcrumbs
				items={[
					{ label: 'Jobs', url: CLIENT.COMPANY.JOBS.TABLE(orgAlias, companyName) },
					{ label: jobCode, url: CLIENT.COMPANY.JOBS.PREVIEW(orgAlias, companyName, jobId) },
					{ label: 'Invoices', onClick: goBackToTable },
					{ label: invoiceId ? 'Edit' : 'New Invoice' },
				]}
			/>
			{renderInvoiceSections()}

		</>
	);

};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const currentFormValues = getFormValues(FORMS.INVOICE_CREATE_FORM)(state) as InvoiceFM;

	const errors = getFormSyncErrors(FORMS.INVOICE_CREATE_FORM)(state) as FormErrorsWithArray<InvoiceFM, string>;

	return {
		companyName: companyData.name,
		isSendingInvoiceNotificationsEnabled: companyData.isSendingInvoiceNotificationsEnabled,
		currentFormValues,
		errors,
		uploadProgress: state.http.uploadProgress,
	};
}

function mapDispatchToProps() {
	return {
		updateInvoicedInvoice: JobActions.updateInvoicedInvoice,
		getInvoiceById: JobActions.getInvoiceById,
		areAutomaticRemindersEnabled: JobActions.areAutomaticRemindersEnabled,
		sendManualReminderForInvoice: JobActions.sendManualReminderForInvoice,
		uploadPendingAttachments: AttachmentActions.uploadPendingAttachments,
		deletePendingAttachments: AttachmentActions.deletePendingAttachments,
		findContactsForInvoices: ContactActions.findForInvoices,
		getAllInvoiceCodesForJob: JobActions.getAllInvoiceCodesForJob,
		getInvoiceReminderRecipients: JobActions.getInvoiceReminderRecipients,
		getUserGroupAndPM: JobActions.getUserGroupAndPM,
	};
}

const formConnector = reduxForm<InvoiceFM>({
	form: FORMS.INVOICE_CREATE_FORM,
	validate: InvoiceFM.validate,
});

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	formConnector,
	connector
);

export default enhance(InvoiceSectionInvoiced);
